import { UseFormReturn } from "react-hook-form";
import { useState } from "react";
import {
  AddExpenseToReportMutationVariables,
  useAddExpenseToReportMutation,
} from "@common/utils/graphql";
import { format } from "date-fns";
import { ExpenseFormValues } from "../forms/Forms";
import { SelectExistingReportContent } from "./SelectExistingReportContent";
import { CreateNewReportContent } from "./CreateNewReportContent";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { useToast } from "@/components/ui/use-toast";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { idempotencyKeyFromObject, receiptInputData } from "@/utils/utils";
import { sanatizeCustomFieldEntries } from "@common/components/CustomFields";
import { usePreventSheetClose } from "@/components/ui/sheet";

interface Props {
  form: UseFormReturn<ExpenseFormValues>;
  prevStep: () => void;
  onComplete: () => void;
}

export const ReportSelectionContent = ({
  form,
  prevStep,
  onComplete,
}: Props) => {
  const [addExpenseToReport, { loading }] = useAddExpenseToReportMutation();
  const { toast } = useToast();

  const [isNew, setIsNew] = useState(false);
  const createNewReport = () => setIsNew(true);
  const selectExistingReport = () => setIsNew(false);
  usePreventSheetClose(() => true);

  const handleBack = () => {
    if (isNew) {
      selectExistingReport();
    } else {
      prevStep();
    }
  };

  const onSubmit = async (reportId: string) => {
    const values = form.getValues();

    const [error, receiptFileData] = await receiptInputData(values.receipt);
    if (error) return;

    const customFieldEntries = sanatizeCustomFieldEntries(
      values.customFieldEntries,
    );
    const params: Omit<AddExpenseToReportMutationVariables, "idempotencyKey"> =
      {
        reportId: Number(reportId),
        expenseCategoryGLId: values.expenseCategoryGLId
          ? Number(values.expenseCategoryGLId)
          : undefined,
        merchantName: values.merchant,
        amount: Number(values.amount),
        notes: values.notes || "",
        transactionDate: format(values.date, "yyyy-MM-dd"),
        receiptFileData: receiptFileData,
        attendeeEmails: values.attendeeEmails,
        transactionId: values.transactionId,
        customFieldEntries,
      };
    try {
      await addExpenseToReport({
        variables: {
          ...params,
          idempotencyKey: idempotencyKeyFromObject(params),
        },
      });
      toast({
        title: "Reimbursement saved successfully",
      });

      onComplete();
    } catch (e) {
      toast({
        title: "Failed to register reimbursement, please try again",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <>
      <SheetBackButton onClick={handleBack} />
      {isNew ? (
        <CreateNewReportContent onSubmit={onSubmit} />
      ) : (
        <SelectExistingReportContent
          onSubmit={onSubmit}
          createNewReport={createNewReport}
        />
      )}
    </>
  );
};
